// schema
import * as ISchema from "@schema";

// util
import { getOrganizerFeatureStatesFromEvent } from "@util/feature-grid.util";
import { niceNameFromCalendarUrnValue } from "@clockwise/client-commons/src/util/event";
import { UrnUtil } from "@clockwise/client-commons/src/util/urn";
import { toGlobalIdForEventParent } from "@util/graphql.util";
import { warnOutsideTests } from "@util/helpers.util";
import { AUTOPILOT_EMOJI } from "@constants/event.constant";

export const roomString = "resource.calendar.google.com";

// get the first event off an eventParent or the event matching the calendarId, if passed
import {
  niceNamesAsArrayWithCarryover,
  niceNameArrayFromPersonsWithCarryover,
} from "@clockwise/client-commons/src/util/event";

export {
  getAttendeeName,
  niceRooms,
  niceNamesAsString,
  niceNameFromPerson,
  niceAttendeeNamesWithCarryover,
  niceNamesAsArrayWithCarryover,
  niceNameArrayFromPersonsWithCarryover,
  getRoomsFromAttendees,
  eventHasRoom,
} from "@clockwise/client-commons/src/util/event";
export {
  isLunchSmartHold,
  isFocusTimeSmartHold,
  isTravelTimeSmartHold,
  isPrepTimeSmartHold,
  isSmartHold,
  isLocalOnlySyncOverride,
  isClockwiseUnsyncedEvent,
  isLinkedSmartHold,
  getTimeAllocation,
  isTeamCalendarOriginEvent,
  isTeamCalendarSyncDisabled,
  isTeamCalendarSyncEvent,
} from "@clockwise/client-commons/src/util/event";

export function isMovementPaused(event: ISchema.IEvent) {
  return !!event?.annotatedEvent?.orgTags?.find(
    (orgTag: any) => orgTag.tag === "MovementPaused" && orgTag.state.active,
  );
}

// this prunes the externalEventId of extraneous instance suffix (i.e. '_20180403...')
// this should really only be used for tracking purposes
export function getBaseExternalEventId(externalEventId: string) {
  if (!externalEventId) {
    return externalEventId; // give us garbage, get back garbage
  }
  const hasLeadingUnderScore = externalEventId[0] === "_";
  const parts = externalEventId.split("_").filter((part) => !!part);
  return (hasLeadingUnderScore ? "_" : "") + parts[0];
}

// create eventParentRelayId
// honestly, this should replace the function below, but I didn't want to change behavior for something so central
function baseCreateEventParentRelayId(orgId: string, externalEventId: string) {
  const eventParentUrn = UrnUtil.createEventParentUrn(externalEventId, orgId);
  const serializedEventParentUrn = UrnUtil.serialize(eventParentUrn);
  const eventParentRelayId = toGlobalIdForEventParent(serializedEventParentUrn);
  return eventParentRelayId;
}

// create eventParentRelayId
export function createEventParentRelayIdFromOrgRelayId(
  orgRelayId: string,
  externalEventId: string,
) {
  if (externalEventId && orgRelayId) {
    const orgId = atob(orgRelayId).split(":")[1];
    return baseCreateEventParentRelayId(orgId, externalEventId);
  }
  return null;
}

// create eventParentRelayId from Event
export function createEventParentRelayIdFromEvent(event: ISchema.IEvent) {
  if (!event || !event.eventKey || !event.eventKey.externalEventId || !event.eventKey.orgId) {
    warnOutsideTests(
      "no event, eventKey, eventKey.externalEventId, org eventKey.orgId; check your fragments!",
    );
    return ""; // if the above warning isn't fixed in dev, this could happen in prod... which is bad
  }
  const { orgId, externalEventId } = event.eventKey;
  return baseCreateEventParentRelayId(orgId, externalEventId);
}

// returns true if the event was synced from personal calendar
export function isEventSyncedFromPersonalCalendar(eventParent: ISchema.IEventParent) {
  if (!eventParent.externalEventId) {
    warnOutsideTests("no externalEventId on eventParent; check your fragments!");
    return;
  }

  // the externalEventId will be prefixed with 'sinced' if it was synced from personal calendar
  // note: 'sinced' is pronounced 'synced'
  return eventParent.externalEventId.indexOf("sinced") === 0;
}

// quick util to show event title or Busy
export function formatEventTitle(hasEvent: { event: ISchema.IEvent | null }): string {
  if (!hasEvent || !hasEvent.event || !hasEvent.event.title || hasEvent.event.title.trim() === "") {
    return "Busy";
  }

  return hasEvent.event.title;
}

export function niceNamesAsArray(nameList: string[], maxCharacters = 10000000) {
  return niceNamesAsArrayWithCarryover(nameList, maxCharacters).niceNameArray;
}

export function isNameInNiceNamesArray(potentialName: string) {
  return potentialName.indexOf(",") === -1 && potentialName.indexOf("and") === -1;
}

// nice names list from attendees and persons
export function niceNameArrayFromPersons(
  persons: ISchema.IPerson[],
  calIds?: string[],
  maxCharacters?: number,
  fullName = false,
  lastInitial = false,
  isLeading = false,
) {
  return niceNameArrayFromPersonsWithCarryover(
    persons,
    calIds,
    maxCharacters,
    fullName,
    lastInitial,
    isLeading,
  ).niceNameArray;
}

// nice names list from attendees and persons
export function niceNameStringFromPersons(
  persons: ISchema.IPerson[],
  calIds?: string[],
  maxCharacters?: number,
  fullName = false,
  lastInitial = false,
) {
  const niceNameArray = niceNameArrayFromPersons(
    persons,
    calIds,
    maxCharacters,
    fullName,
    lastInitial,
    true,
  );
  return niceNameArray.join("");
}

// nice names list from attendees and profiles
export function niceAttendeeNames(
  attendees: ISchema.IAttendee[],
  persons: ISchema.IPerson[],
  maxCharacters?: number,
  _fullName = false,
) {
  const calIds = attendees
    .filter((attendee) => {
      if (!attendee || !attendee.urnValue) {
        warnOutsideTests("no urnValue on attendee; check your fragments!");
        return false;
      }
      const isRoom = attendee.urnValue.indexOf(roomString) > -1;
      return !isRoom;
    })
    .map((attendee) => attendee.urnValue);
  return niceNameStringFromPersons(persons, calIds, maxCharacters);
}

type PersonProfile = {
  __typename: "UserProfile";
  givenName: string | null;
  familyName: string | null;
  externalImageUrl: string | null;
} | null;
export function niceName(
  person?: { profile: PersonProfile } | null,
  calendarId?: string | null,
  fullName = false,
) {
  if (!person && !calendarId) {
    return "";
  } else if (
    fullName &&
    person &&
    person.profile &&
    person.profile.givenName &&
    person.profile.familyName
  ) {
    return `${person.profile.givenName} ${person.profile.familyName}`;
  } else if (person && person.profile && person.profile.givenName) {
    return person.profile.givenName;
  } else if (!!calendarId) {
    return niceNameFromCalendarUrnValue(calendarId);
  } else {
    return "";
  }
}

export function nonRoomCalendarIds(attendees: ISchema.IAttendee[]) {
  return attendees
    .filter((attendee) => {
      if (!attendee || !attendee.urnValue) {
        warnOutsideTests("no urnValue on attendee; check your fragments!");
        return false;
      }
      const isRoom = attendee.urnValue.indexOf(roomString) > -1;
      return !isRoom;
    })
    .map((attendee) => attendee.urnValue);
}

export function removeAutopilotEmoji(title: string | null) {
  if (title && title.indexOf(AUTOPILOT_EMOJI) <= 1) {
    return title.replace(`${AUTOPILOT_EMOJI} `, "");
  }
  return title;
}

export const isApPausedFromFeatureGrid = (event: ISchema.IEvent) => {
  const featureStates = event && getOrganizerFeatureStatesFromEvent(event);
  const autopilotPausedFromFeatureGrid = featureStates
    ? featureStates.mixedFreemiumOrgUIState.autopilotPaused
    : false;

  return autopilotPausedFromFeatureGrid;
};

export const isMoveRangeEnabledFromFeatureGrid = (event: ISchema.IEvent) => {
  const featureStates = event && getOrganizerFeatureStatesFromEvent(event);
  const moveRangesEnabledFromFeatureGrid = featureStates
    ? featureStates.mixedFreemiumOrgUIState.moveRangesEnabled
    : true;

  return moveRangesEnabledFromFeatureGrid;
};

export const getOrganizerNameAndEmailFromAttendeeList = (
  organizerUrnValue: string | undefined,
  attendeePeople: {
    list: Array<{ primaryEmail: string; profile?: { givenName?: string } }>;
  } | null,
) => {
  // We will always have the organizer email at this point, email can be populated early
  let organizerForDisplay: { name?: string; email?: string } = {};
  organizerForDisplay.email = organizerUrnValue ? organizerUrnValue : undefined;

  if (!attendeePeople) return organizerForDisplay;
  const organizerPerson = attendeePeople.list.find((person) => {
    return organizerUrnValue === person.primaryEmail;
  });

  // If name of organizer can be found, add it here
  if (organizerPerson) {
    organizerForDisplay.name =
      organizerPerson && organizerPerson.profile ? organizerPerson.profile.givenName : undefined;
  }

  return organizerForDisplay;
};
